import * as React from "react";
import { SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkPage, toLinkSpace } from "../identity/useNavMenu";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { linksOf } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Location } from "../../schema/types/location/entity";
import { LocationDetailHeader } from "./header";
import { Summary } from "./summary";
import { UsedInGVCs } from "./usedInGvcs";
import { CpRootError } from "../gvc/testNewForm/CpRootError";

interface Props {
  location: Location;
  refetch?: () => void;
}
export const LocationDetailContent: React.FC<Props> = ({ location, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/location/:location/*`);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Used in GVCs", "/used"),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting]);

  return (
    <BasePathContext.Provider value={basePath}>
      <LocationDetailHeader location={location} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary location={location} />} />
              <Route path={`used/*`} element={<UsedInGVCs location={location} />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"location"}
                    itemName={location.name}
                    resourceId={location.id}
                    version={location.version}
                    selfLink={linksOf(location).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(location).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
